import { mergeWith } from 'lodash-es';

interface TeaConfig {
  user_unique_id?: string;
  user_id?: string;
  uid_type?: number;
  shop_region?: string;
  evtParams?: Record<string, any>;
}

declare const isOnline: boolean;

export const configTea = (config: TeaConfig, start = false): void => {
  const { Tea } = window;
  Tea?.config({
    ...config,
    _staging_flag: Number(!isOnline) as 0 | 1,
  });
  if (start) {
    setTimeout(() => {
      Tea?.start();
    });
  }
};

export const startTea = (config?: TeaConfig) => {
  const { Tea } = window;
  if (config) {
    Tea?.config({
      ...config,
      _staging_flag: Number(!isOnline) as 0 | 1,
    });
  }
  setTimeout(() => {
    Tea?.start();
  });
};

let commonParams = {};

export const addCommonTeaParams = (payload: Record<string, any>) => {
  const { Tea } = window;
  // ignore the empty string value
  commonParams = mergeWith({ ...commonParams }, payload, (target, source) =>
    source === '' && target ? target : undefined,
  );
  Tea?.config({
    evtParams: commonParams,
    _staging_flag: Number(!isOnline) as 0 | 1,
  });
};

export const sendTeaLog = (
  key: string,
  payload?: Record<string, any>,
  sendBeacon?: boolean,
) => {
  if (sendBeacon) {
    window.Tea?.beconEvent(key, { ...payload } || {});
  } else if (typeof window.Tea?.event === 'function') {
    window.Tea?.event?.(key, { ...payload });
  } else {
    window.Tea?.(key, { ...payload });
  }
};

/**
 * Factory method to create a typesafe TEA logger for a projects specific ByteIO events
 * @EventParamsMap The map of event names to their payload types generate by et-tool
 * @CommonParams These are common required TEA params that area already being send by default
 * as they were configured using addCommonTeaParams(). So they will be ommited from explicitly
 * being sent here
 * @returns sendTeaLogSafe function to send TEA events
 */
export const generatorTeaLogger = <EventParamsMap, CommonParams = {}>() => {
  // Get properties of event
  type EventsType = Extract<keyof EventParamsMap, string>;

  // Turn all CommonParams in the event optional
  type EventParams<EventName extends EventsType> = Partial<
    EventParamsMap[EventName]
  > &
    Omit<EventParamsMap[EventName], keyof CommonParams>;

  const sendTeaLogSafe = <EventName extends EventsType>(
    eventName: EventName,
    eventParams: EventParams<EventName>,
    sendBeacon?: boolean,
  ): void => {
    if (sendBeacon) {
      window.Tea?.beconEvent(eventName, { ...eventParams } || {});
    } else if (typeof window.Tea?.event === 'function') {
      window.Tea?.event?.(eventName, { ...eventParams });
    } else {
      window.Tea?.(eventName, { ...eventParams });
    }
  };

  return sendTeaLogSafe;
};

export const getTeaVar = (name: string, defaultValue: unknown) =>
  new Promise((resolve, reject) => {
    if (typeof window.Tea?.getVar === 'function') {
      // `resolve` will be called back when the variable value is obtained
      window.Tea.getVar(name, defaultValue, resolve);
    } else {
      reject(new Error('window.Tea.getVar is not exist'));
    }
  });
